import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { mapDirectorToEditForm } from '../../utils/apiMappers';
import { buildDirectorUpdatePayload, normalizeToIsoDate } from '../../utils/buildDirectorFormData';
import PersonalInfoStep from './steps/PersonalInfoStep';
import ContactInfoStep from './steps/ContactInfoStep';

const STEPS = ['Personal Info', 'Contact Info', 'Assignment'];

const EditDirector = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formData, setFormData] = useState({
    companyId: '',
    companyName: '',
    firstName: '',
    lastName: '',
    emiratesId: '',
    passportNumber: '',
    nationality: '',
    dateOfBirth: null,
    gender: '',
    email: '',
    mobileNumber: '',
    address: '',
    position: '',
    appointmentDate: null,
    passportExpiryDate: null,
    emiratesIdExpiryDate: null,
    passportFile: null,
    emiratesIdFile: null,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadDirector = async () => {
      try {
        setFetching(true);
        setLoadError('');

        const response = await csAdminApi.getDirectorById(id);
        const row = response.data?.data;
        if (!row) {
          setLoadError('Director not found');
          return;
        }
        const mapped = mapDirectorToEditForm(row);
        if (mapped) setFormData(mapped);
      } catch (err) {
        console.error('Failed to load director:', err);
        setLoadError('Failed to load director details');
      } finally {
        setFetching(false);
      }
    };

    loadDirector();
  }, [id]);

  const handleFormChange = (next) => {
    setFormData(next);
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.emiratesId) newErrors.emiratesId = 'Emirates ID is required';
      if (!formData.passportNumber) newErrors.passportNumber = 'Passport number is required';
      if (!formData.nationality) newErrors.nationality = 'Nationality is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (step === 1) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.mobileNumber) newErrors.mobileNumber = 'Mobile number is required';
    }

    if (step === 2) {
      if (!formData.position) newErrors.position = 'Position is required';
      if (!formData.appointmentDate) newErrors.appointmentDate = 'Appointment date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const uploadIdentityFile = async (category, file, documentNumber, expiryDate) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('owner_type', 'DIRECTOR');
    fd.append('owner_id', String(id));
    if (formData.companyId) fd.append('company_id', String(formData.companyId));
    fd.append('doc_category', category);
    fd.append('document_name', file.name);
    if (documentNumber) fd.append('documentNumber', documentNumber);
    const isoExpiry = normalizeToIsoDate(expiryDate);
    if (isoExpiry) fd.append('expiryDate', isoExpiry);
    await csAdminApi.uploadDocument(fd);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    try {
      setLoading(true);

      const payload = buildDirectorUpdatePayload(formData);
      await csAdminApi.updateDirector(id, payload);

      if (formData.passportFile) {
        await uploadIdentityFile(
          'passport',
          formData.passportFile,
          formData.passportNumber,
          formData.passportExpiryDate
        );
      }
      if (formData.emiratesIdFile) {
        await uploadIdentityFile(
          'emirates_id',
          formData.emiratesIdFile,
          formData.emiratesId,
          formData.emiratesIdExpiryDate
        );
      }

      enqueueSnackbar('Director updated successfully', { variant: 'success' });
      navigate(`/directors/${id}`);
    } catch (err) {
      console.error('Failed to update director:', err);
      enqueueSnackbar('Failed to update director', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderAssignmentStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assignment Details
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Update position and status for {formData.companyName || 'this company'}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company"
            value={formData.companyName || ''}
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Position/Title"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            error={!!errors.position}
            helperText={errors.position}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Appointment Date *"
            value={formData.appointmentDate}
            onChange={(value) => setFormData({ ...formData, appointmentDate: value })}
            inputFormat="dd/MM/yyyy"
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                error={!!errors.appointmentDate}
                helperText={errors.appointmentDate}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PersonalInfoStep
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            includeIdentityDocuments
          />
        );
      case 1:
        return (
          <ContactInfoStep
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
          />
        );
      case 2:
        return renderAssignmentStep();
      default:
        return null;
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ px: 3, py: 3 }}>
        <Alert severity="error">{loadError}</Alert>
        <Button onClick={() => navigate('/directors')} sx={{ mt: 2 }}>
          Back to Directors
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Director
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Update director profile and assignment details
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 3, mb: 3 }}>
        {renderStepContent()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate(`/directors/${id}`)} disabled={loading}>
            Cancel
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EditDirector;
